import { Component, Input, OnChanges, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import { GetUrlResponse } from 'app/auth/models/get-url-response.model';
import { AuthService } from 'app/auth/services/auth.service';
import { Theme } from 'app/models/themes/theme.model';
import { UnsubscribeComponent } from 'app/shared-components/components/unsubscribe/unsubscribe.abstract';
import { environment } from '../../../../environments/environment';
import { APP_CONFIG } from 'app/shared-services/services/config.service';
import { GlobalCTATrackerService } from 'app/shared-services/services/global-cta-tracker.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PlatFormRequest } from 'app/auth/models/plat-form-request';
import { MobileTracking } from 'app/auth/models/mobile-tracking.model';
import { SessionStorageService } from 'app/shared-services/services/session-storage.service';
import { UrlScheme } from 'app/models/url-scheme.model';

@Component({
    selector: 'app-custom-policy',
    templateUrl: './custom-policy.component.html',
    styleUrls: ['./custom-policy.component.scss'],
    standalone: false
})
export class CustomPolicyComponent extends UnsubscribeComponent implements OnInit, OnDestroy, OnChanges {
    @Input() registration: boolean = false;
    @Input() reset: boolean = false;
    @Input() signIn: boolean = false;
    @Input() theme: Theme = new Theme();
    @Input() b2cURLs: GetUrlResponse;
    @Input() exId: string = '';
    @Input() mobileTracking: MobileTracking;
    @Input() mobileUrlScheme: UrlScheme;
    @Input() invitationKey: string = '';
    @Input() isFromMobile: boolean = false;
    @Input() beginRegistrationTrigger: number = 0;
    @Input() beginChangePasswordTrigger: number = 0;
    @Input() assetVerificationRequestId: string = '';
    @Input() freshDeskUrl: string = '';

    public maxAttempts = 10;

    public defaultWebClient = 'PFM Web';

    public signInUrl: string = '';
    public registrationUrl: string = '';
    public resetUrl: string = '';
    public returnUrl: string = '';

    constructor(
        private authService: AuthService,
        private deviceService: DeviceDetectorService,
        private globalCTATrackerService: GlobalCTATrackerService,
        private sessionStorageService: SessionStorageService
    ) {
        super();
    }

    ngOnInit(): void {
        this.buildUrls();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['beginRegistrationTrigger'] && !changes['beginRegistrationTrigger'].firstChange) || this.beginRegistrationTrigger > 0) {
            this.tryOpenRegistration();
        }

        if (changes['beginChangePasswordTrigger'] && !changes['beginChangePasswordTrigger'].firstChange) {
            this.tryOpenChangePassword();
        }
    }

    private buildUrls(): void {
        this.sessionStorageService.setItem('companyCode', this.theme?.companyCode);

        if (this.signIn) {
            this.emitEvent('Build Sign In Widget');
        } else {
            this.emitEvent('Build Registration Widget');
        }

        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

        let verifier = '';

        const randomValues = new Uint8Array(64);
        window.crypto.getRandomValues(randomValues);
        for (let i = 0; i < 64; i++) {
            verifier += charset[randomValues[i] % charset.length];
        }

        this.authService.codeVerifier = verifier;

        const base64UrlEncode = (buffer: ArrayBuffer | Uint8Array): string => {
            const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) { binary += String.fromCharCode(bytes[i]); }
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        };
        const pkceChallenge = async (v: string): Promise<string> => {
            const data = new TextEncoder().encode(v);
            const hash = await crypto.subtle.digest('SHA-256', data);
            return base64UrlEncode(hash);
        };

        const color = this.theme?.themePrimaryColor?.mainColor || '';
        const pc = color ? (color.startsWith('#') ? color.substring(1) : color) : '';
        const colorParam = pc ? '&color=' + encodeURIComponent(pc) : '';

        const platFormRequest = new PlatFormRequest({
            appVersion: environment.appVersion,
            browserType: this.deviceService.getDeviceInfo().browser,
            deviceType: this.deviceService.getDeviceInfo().os,
            osVersion: this.deviceService.getDeviceInfo().os_version,
            platForm: 'Web',
            userAgent: navigator.userAgent
        });

        pkceChallenge(verifier).then(challenge => {
            const currentUrl = new URL(window.location.href);
            this.returnUrl = currentUrl.href;
            this.authService.b2cUrls = this.b2cURLs;

            if (APP_CONFIG.environment !== 'dev') {
                if (this.isFromMobile) {
                    this.signInUrl = this.b2cURLs?.b2CLoginUrl + colorParam;
                    this.registrationUrl = this.b2cURLs?.b2CSignUpUrl + colorParam + `&invitation=${this.invitationKey}` + `&signInUrl=${currentUrl.origin}/pfm/login`;
                } else {
                    this.signInUrl = this.b2cURLs?.webB2CLoginUrl + colorParam;
                    this.registrationUrl = this.b2cURLs?.webB2CSignUpUrl + colorParam + `&invitation=${this.invitationKey}` + `&signInUrl=${currentUrl.origin}/pfm/login`;
                }

                this.resetUrl = this.b2cURLs?.webB2CResetPasswordUrl + colorParam;
            } else {
                this.signInUrl = APP_CONFIG.signInUrl + colorParam + '&companyCode=' + this.theme.companyCode + '&local=true';
                this.registrationUrl = APP_CONFIG.registrationUrl + colorParam + `&invitation=${this.invitationKey}` + '&companyCode=' + this.theme.companyCode + `&signInUrl=${currentUrl.origin}/pfm/login`;
                this.resetUrl = APP_CONFIG.resetPasswordUrl + colorParam;

                this.authService.b2cUrls.b2CRefreshTokenBaseUrl = APP_CONFIG.b2CRefreshTokenBaseUrl;
                this.authService.b2cUrls.b2CInfo.redirectUriEncoded = APP_CONFIG.redirectUriEncoded;
            }

            let errorMessageParam = currentUrl.searchParams.get('errorMessage');

            this.signInUrl += `&code_challenge=${challenge}` +
                `&code_challenge_method=S256` +
                `&titleBar=${encodeURIComponent(this.theme.titleBar || '')}` +
                `&logo=${encodeURIComponent(this.theme.themeSecondaryLogo?.url || '')}` +
                `&app_version=${this.mobileTracking?.app_version ?? platFormRequest.appVersion}` +
                `&browser_type=${this.mobileTracking?.browserType ?? platFormRequest.browserType}` +
                `&device_type=${this.mobileTracking?.deviceType ?? platFormRequest.deviceType}` +
                `&os_version=${this.mobileTracking?.osVersion ?? platFormRequest.osVersion}` +
                `&platform=${this.mobileTracking?.platForm ?? platFormRequest.platForm}` +
                `&user_agent=${encodeURIComponent(platFormRequest.userAgent)}` +
                `&appBuildNumber=${this.mobileTracking?.appBuildNumber ?? environment.appVersion}` +
                `&appClient=${this.mobileTracking?.appClient ?? this.theme.companyCode}` +
                `&appPlatform=${this.mobileTracking?.appPlatform ?? this.defaultWebClient}` +
                `&appVersion=${this.mobileTracking?.appVersion ?? environment.appVersion}` +
                `&flow=${this.mobileTracking?.flow}` +
                `&returnUrl=${encodeURIComponent(this.returnUrl)}` +
                `&freshDeskUrl=${encodeURIComponent(this.freshDeskUrl)}` +
                `&errorMessage=${encodeURIComponent(errorMessageParam)}` +
                `&mixpanelUserTemporaryId=${this.sessionStorageService.getItem('mixpanelUserTemporaryId')}`;

            this.registrationUrl +=
                `&code_challenge=${challenge}` +
                `&code_challenge_method=S256` +
                `&mobile=${this.isFromMobile}` +
                `&titleBar=${encodeURIComponent(this.theme.titleBar || '')}` +
                `&logo=${encodeURIComponent(this.theme.themeSecondaryLogo?.url || '')}` +
                `&app_version=${this.mobileTracking?.app_version ?? platFormRequest.appVersion}` +
                `&browser_type=${this.mobileTracking?.browserType ?? platFormRequest.browserType}` +
                `&device_type=${this.mobileTracking?.deviceType ?? platFormRequest.deviceType}` +
                `&os_version=${this.mobileTracking?.osVersion ?? platFormRequest.osVersion}` +
                `&platform=${this.mobileTracking?.platForm ?? platFormRequest.platForm}` +
                `&user_agent=${encodeURIComponent(platFormRequest.userAgent)}` +
                `&appBuildNumber=${this.mobileTracking?.appBuildNumber ?? environment.appVersion}` +
                `&appClient=${this.mobileTracking?.appClient ?? this.theme.companyCode}` +
                `&appPlatform=${this.mobileTracking?.appPlatform ?? this.defaultWebClient}` +
                `&appVersion=${this.mobileTracking?.appVersion ?? environment.appVersion}` +
                `&flow=${this.mobileTracking?.flow}` +
                `&returnUrl=${encodeURIComponent(this.returnUrl)}` +
                `&freshDeskUrl=${encodeURIComponent(this.freshDeskUrl)}` +
                `&mobileUrlScheme=${encodeURIComponent(this.mobileUrlScheme?.scheme + '/deeplink?redirectUrl=LoginPage' || '')}` +
                `&mixpanelUserTemporaryId=${this.sessionStorageService.getItem('mixpanelUserTemporaryId')}`;

            this.resetUrl +=
                `&code_challenge=${challenge}` +
                `&code_challenge_method=S256` +
                `&mobile=${this.isFromMobile}` +
                `&titleBar=${encodeURIComponent(this.theme.titleBar || '')}` +
                `&logo=${encodeURIComponent(this.theme.themeSecondaryLogo?.url || '')}` +
                `&app_version=${this.mobileTracking?.app_version ?? platFormRequest.appVersion}` +
                `&browser_type=${this.mobileTracking?.browserType ?? platFormRequest.browserType}` +
                `&device_type=${this.mobileTracking?.deviceType ?? platFormRequest.deviceType}` +
                `&os_version=${this.mobileTracking?.osVersion ?? platFormRequest.osVersion}` +
                `&platform=${this.mobileTracking?.platForm ?? platFormRequest.platForm}` +
                `&user_agent=${encodeURIComponent(platFormRequest.userAgent)}` +
                `&appBuildNumber=${this.mobileTracking?.appBuildNumber ?? environment.appVersion}` +
                `&appClient=${this.mobileTracking?.appClient ?? this.theme.companyCode}` +
                `&appPlatform=${this.mobileTracking?.appPlatform ?? this.defaultWebClient}` +
                `&appVersion=${this.mobileTracking?.appVersion ?? environment.appVersion}` +
                `&flow=${this.mobileTracking?.flow}` +
                `&returnUrl=${encodeURIComponent(this.returnUrl)}` +
                `&freshDeskUrl=${encodeURIComponent(this.freshDeskUrl)}` +
                `&mixpanelUserTemporaryId=${this.sessionStorageService.getItem('mixpanelUserTemporaryId')}`;

            this.sessionStorageService.setItem('pkce_code_verifier', verifier);
            this.sessionStorageService.setItem('client_id', this.b2cURLs.b2CInfo.clientId);
            this.sessionStorageService.setItem('scope', this.b2cURLs.b2CInfo.scope);
            this.sessionStorageService.setItem('redirectUriEncoded', this.authService.b2cUrls.b2CInfo.redirectUriEncoded);
            this.sessionStorageService.setItem('b2CRefreshTokenBaseUrl', this.authService.b2cUrls.b2CRefreshTokenBaseUrl);
            this.sessionStorageService.setItem('isMobile', 'true');
            this.sessionStorageService.setItem('b2cURLs', JSON.stringify(this.b2cURLs));

            let returnUrlParam = currentUrl.searchParams.get('returnUrl') ?? currentUrl.searchParams.get('redirectUrl');
            if (returnUrlParam) {
                returnUrlParam = decodeURIComponent(returnUrlParam);
            }
            let targetPath = returnUrlParam && returnUrlParam.trim() ? returnUrlParam.trim() : currentUrl.pathname;

            // remove duplicate code in the target path
            if (targetPath.includes('code')) {
                targetPath = this.removeCodeParameter(targetPath);
            }

            if (!targetPath.startsWith('/')) {
                targetPath = '/' + targetPath;
            }

            if (targetPath === '/' || targetPath === null || targetPath === '/pfm/login' || targetPath === '/pfm/registration/invite') {
                targetPath = '/dashboard';
            }

            if (!targetPath.startsWith('/pfm')) {
                targetPath = '/pfm' + targetPath;
            }

            if (this.exId) {
                this.registrationUrl += `&exid=${this.exId}`;
            }

            let postAuthRedirectBase = currentUrl.origin + targetPath;

            if (this.assetVerificationRequestId) {
                this.registrationUrl += `&assetVerificationRequestId=${this.assetVerificationRequestId}`;
                postAuthRedirectBase = currentUrl.origin + `/pfm/verification-service/loan?key=${this.assetVerificationRequestId}`;
            }

            this.sessionStorageService.setItem('post_auth_redirect_base', postAuthRedirectBase);

            if (this.signIn) {
                this.sessionStorageService.setItem('isSignIn', 'true');
                window.open(this.signInUrl, '_self');
            }

            if (this.registration && this.isFromMobile) {
                window.open(this.registrationUrl, '_self');
            }
        });
    }

    private removeCodeParameter(url: string): string {
        // Remove the code parameter and its value
        let cleanUrl = url.replace(/[?&]code=[^&]*/, '');

        // Fix any double ampersands that might result
        cleanUrl = cleanUrl.replace(/&&+/g, '&');

        // If URL starts with &, replace it with ?
        cleanUrl = cleanUrl.replace(/^([^?]*)\&/, '$1?');

        return cleanUrl;
    }

    private tryOpenRegistration(attempt: number = 0): void {
        if (!this.registration) return;
        if (this.registrationUrl) {
            window.open(this.registrationUrl, '_self');
            return;
        }

        if (attempt < this.maxAttempts) {
            const delayMs = 250 + attempt * 50;
            setTimeout(() => this.tryOpenRegistration(attempt + 1), delayMs);
        }
    }

    private tryOpenChangePassword(): void {
        if (this.resetUrl) {
            window.open(this.resetUrl, '_self');
            return;
        }
    }

    private emitEvent(cta: string): void {
        this.globalCTATrackerService.unAuthenticatedEventTrackerV2('login-cta', cta);
    }
}
